import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { UpdateCostCenterDocument } from 'generated/resources/graphql'
import { noop } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface SaveProps {
  id: string
  code: string
  description: string
  name: string
  onSuccess?: () => void
  onError?: (errors: Record<string, string>) => void
}

export const useStructuredCostCenterMutations = (refetchQueries?: TypedDocumentNode[]) => {
  const [updateStructuredCostCenter] = useMutation(UpdateCostCenterDocument, {
    extractorFn: (newData) => newData.costCentersMutations.updateCostcentersCostCenter,
    ...(refetchQueries && { refetchQueries }),
  })

  const manageAction = ({
    response,
    onSuccess,
    onError,
    successMessage,
  }: {
    response: UnhandledResult<Result<unknown>>
    onSuccess?: () => void
    onError?: (errors: Record<string, string>) => void
    successMessage?: string
  }) => {
    const result = handleResult(response, onError || noop)

    if (result.success) {
      onSuccess?.()

      app.setFlash({
        type: 'correct',
        message: successMessage || i18n.t('finance.cost_center_detail.sidepanel.success'),
      })
    }

    return result
  }

  const save = async ({ id, name, code, description, onSuccess, onError }: SaveProps) => {
    const response = await updateStructuredCostCenter({
      variables: {
        id: id,
        name: name,
        code: code,
        description: description,
      },
      update: (cache, { data }) => {
        const updatedCostCenter =
          data?.costCentersMutations?.updateCostcentersCostCenter?.costCenter
        if (!updatedCostCenter) return

        // With keyFields: ['id', 'jobCatalogNodeUuid'], we need both fields to identify the object
        cache.modify({
          id: cache.identify({
            __typename: 'CostCentersStructuredCostCenter',
            id,
            structure: null,
          }),
          fields: {
            name: () => updatedCostCenter.name,
            code: () => updatedCostCenter.code,
          },
        })
      },
    })

    return manageAction({ response, onSuccess, onError })
  }

  return {
    save,
  }
}
